import { StrictMode, type ComponentType } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App.tsx";
import { DesignToolsApp } from "./DesignToolsApp.tsx";
import { SpecLensApp } from "./SpecLensApp.tsx";
import { TemplateCreatorApp } from "./TemplateCreatorApp.tsx";
import { ErrorBoundary } from "./ErrorBoundary.tsx";
import "./app.css";

const root = document.getElementById("root");
if (root === null) throw new Error("index.html is missing #root.");

// A manual switch on the path rather than a router: the screens link to each other with plain
// anchors, and every path lands on /index.html either way (CloudFront's SPA fallback), so this
// is the only place that has to know any of the paths exist. Anything unmatched, including /,
// is the DesignTools front door.
//
// Signing in from a cold session leaves a path like /speclens for Entra and comes back on
// /auth/callback, not that path - a plain pathname check would strand a user on the front door
// the moment they finish signing in. sessionStorage survives that round trip in the same tab:
// each app path records its own name on the way out, and /auth/callback reads it back; every
// other path clears it, so the flag cannot outlive the one redirect it is for.
const APPS: Readonly<Record<string, ComponentType>> = {
  speclens: SpecLensApp,
  templatecreator: TemplateCreatorApp,
  planforge: App,
};

const STORAGE_KEY = "plan-forge:last-app";
const path = window.location.pathname;
const pathApp = Object.keys(APPS).find((name) => path === `/${name}` || path.startsWith(`/${name}/`));

let rememberedApp: string | null = null;
try {
  rememberedApp = sessionStorage.getItem(STORAGE_KEY);
  if (pathApp !== undefined) sessionStorage.setItem(STORAGE_KEY, pathApp);
  else if (!path.startsWith("/auth/callback")) sessionStorage.removeItem(STORAGE_KEY);
} catch {
  // Private browsing or storage disabled: sign-in still works, it just always lands on the front
  // door after a redirect from one of these paths rather than back on the right one.
}

const resolvedName = pathApp ?? (path.startsWith("/auth/callback") ? rememberedApp : null);
const CurrentApp = (resolvedName !== null ? APPS[resolvedName] : undefined) ?? DesignToolsApp;

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary>
      <CurrentApp />
    </ErrorBoundary>
  </StrictMode>,
);
